import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import FloatingOrderButton from "../components/FloatingOrderButton";
import ConfirmationModal from "../Modals/ConfirmationModal";
import ItemModal from "../Modals/ItemModal";
import EditModal from "../Modals/EditModal";
import {getMenu, getMenuList} from "../api/apis";

export default function OrderPage() {

    const [categories, setCategories] = useState([]);
    const [data, setData] = useState({});
    const [activeCategory, setActiveCategory] = useState("0001");

    const navbarRef = useRef(null);

    const [showOrder, setShowOrder] = useState(false);
    const [orderItem, setOrderItem] = useState(null);
    const [sum, setSum] = useState(0);
    const [order, setOrder] = useState([]);
    const [editIndex, setEditIndex] = useState(-1);

    // Fetch categories and their items
    useEffect(() => {
        const fetchData = async () => {
            try {
                const cat = await getMenuList();
                setCategories(cat);
                //console.log(cat)

                for (const category of cat) {
                    const items = await getMenu(category.id);
                    setData(prev => ({
                        ...prev,
                        [category.id]: items
                    }));
                }
            } catch (error) {
                console.error("Error fetching categories or items:", error);
            }
        };

        fetchData();

        const storage = JSON.parse(localStorage.getItem("cart"));
        setOrder(storage?.items ?? []);
        setSum(storage?.sum ?? 0);
    }, []);

    // Scroll navbar to active category
    useEffect(() => {
        if (navbarRef.current) {
            const activeTab = navbarRef.current.querySelector(".active-category");
            if (activeTab) {
                activeTab.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "center"
                });
            }
        }
    }, [activeCategory]);

    // Scroll detection for category highlight
    useEffect(() => {
        const handleScroll = () => {
            let current = "0001";
            categories.forEach((category) => {
                const section = document.getElementById(category.id);
                if (section) {
                    const sectionTop = section.getBoundingClientRect().top;
                    const viewportHeight = window.innerHeight;

                    if (sectionTop < viewportHeight / 3) {
                        current = category.id;
                    }
                }
            });
            setActiveCategory(current);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [categories]);

    function addToCart(item) {
        const updatedOrder = [...order, {
            ...item,
            finalPrice: item.finalPrice ?? item.price,
            customizations: item.customizations ?? { add_ons: [], sides: [], removed: [] }
        }];
        const updatedSum = sum + (item.finalPrice ?? item.price) / 100;

        setOrder(updatedOrder);
        setSum(updatedSum);
        localStorage.setItem("cart", JSON.stringify({ items: updatedOrder, sum: updatedSum }));
    }

    function editItem(newItem) {
        const updatedOrder = [...order];
        updatedOrder[editIndex] = newItem;
        const updatedSum = updatedOrder.reduce((acc, item) => acc + (item.finalPrice ?? item.price) / 100, 0);

        setOrder(updatedOrder);
        setSum(updatedSum);
        localStorage.setItem("cart", JSON.stringify({ items: updatedOrder, sum: updatedSum }));
    }

    function removeFromCart(index) {
        if (index < 0 || index >= order.length) return;

        const updatedOrder = [...order];
        const removedItem = updatedOrder.splice(index, 1)[0];

        if (!removedItem) return;

        const itemPrice = (removedItem.finalPrice ?? removedItem.price) / 100;
        const updatedSum = Math.max(sum - itemPrice, 0);

        setOrder(updatedOrder);
        setSum(updatedSum);
        localStorage.setItem("cart", JSON.stringify({ items: updatedOrder, sum: updatedSum }));
        setEditIndex(prev => (prev === -1 ? -2 : -1));
    }

    function clearCart() {
        setSum(0);
        setOrder([]);
        localStorage.removeItem("cart");
    }

    return (
        <div>
            {/* Fixed Category Navbar */}
            <div className="position-fixed bg-white shadow-sm m-auto"
                style={{ zIndex: "1000", top: "50px", width: "100vw", maxWidth: "500px" }}>
                <div className="container">
                    <div
                        className="d-flex overflow-auto py-1 gap-2"
                        ref={navbarRef}
                        style={{
                            whiteSpace: "nowrap",
                            overflowX: "auto",
                            overflowY: "hidden",
                            scrollBehavior: "smooth"
                        }}
                    >
                        {categories.map((category) => (
                            <div key={category.id} className="nav-item me-3 position-relative">
                                <a
                                    className={`nav-link text-dark pb-1 ${activeCategory === category.id ? "active-category" : ""}`}
                                    href={`#${category.id}`}
                                    style={{
                                        whiteSpace: "nowrap",
                                        fontWeight: "600",
                                        fontSize: "1.05rem",
                                        textTransform: "capitalize",
                                        position: "relative",
                                    }}
                                >
                                    {category.name.replace("_", " ")}
                                    <span
                                        className="position-absolute start-50 translate-middle-x"
                                        style={{
                                            bottom: "0",
                                            width: activeCategory === category.id ? "110%" : "0%",
                                            height: "2px",
                                            backgroundColor: "#ffc107",
                                            borderRadius: "3px",
                                            transition: "width 0.3s ease-in-out",
                                        }}
                                    ></span>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Page Content */}
            <div className="pt-5 mt-5" style={{ paddingTop: "110px", paddingBottom: "5rem" }}>
                {categories.map((category, index) => (
                    <div key={index} id={category.id} className="mb-4" style={{ scrollMarginTop: "100px" }}>
                        <h4 className="text-center fw-bold text-dark">
                            {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
                        </h4>
                        <div className="d-flex flex-column justify-content-center gap-3">
                            {data[category.id]?.map((item) => (
                                <div key={item.plu}>
                                    <div className="card border-0 shadow-sm h-100"
                                        style={{ borderRadius: "15px", overflow: "hidden", background: "#fff" }}>
                                        <div className="p-3">
                                            <h6 className="fw-bold text-dark mb-1">{item.text.toSwe()}</h6>
                                            <p className="text-muted small m-0">{item.desc.toSwe()}</p>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center px-3 pb-3">
                                            <p className="m-0 fw-bold fs-6 text-muted">
                                                {(item.price / 100)} kr
                                            </p>
                                            <div className="d-flex gap-1">
                                                <button
                                                    className="btn btn-warning btn-sm px-3 rounded-3 fw-semibold"
                                                    onClick={() => addToCart(item)}
                                                >
                                                    Lägg till
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm rounded-3 fw-semibold"
                                                    onClick={() => setOrderItem(item)}
                                                >
                                                    <i className="bi bi-pencil-square" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Floating Order Button and Modals */}
            <FloatingOrderButton order={order} sum={sum} setShowModal={setShowOrder} />
            {editIndex > -1 && order[editIndex] ? (
  <EditModal
    item={order[editIndex]}
    setItem={() => setEditIndex(-1)}
    add={editItem}
  />
) : orderItem ? (
  <ItemModal
    item={orderItem}
    setItem={setOrderItem}
    add={addToCart}
  />
) : showOrder ? (
  <ConfirmationModal
    showOrder={showOrder}
    setShowOrder={setShowOrder}
    removeFromCart={removeFromCart}
    clear={clearCart}
    sum={sum}
    editCart={setEditIndex}
    refresh={editIndex}
  />
) : null}

        </div>
    );
}
